import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { uid } from "@/lib/uid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Check, Search } from "lucide-react";
import PericoloCard from "./PericoloCard";

const CAT_BADGE = {
  fisico: "bg-rose-100 text-rose-700 border-rose-200",
  chimico: "bg-amber-100 text-amber-700 border-amber-200",
  biologico: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ergonomico: "bg-sky-100 text-sky-700 border-sky-200",
  organizzativo: "bg-violet-100 text-violet-700 border-violet-200",
};

/**
 * StepPericoli — Step 5 DVR wizard.
 * Multi-select da catalogo standardizzato (D.I. 30/11/2012) di ~80 pericoli
 * raggruppati per gruppo tematico. L'utente seleziona i pericoli applicabili
 * all'azienda e clicca "Genera valutazioni" per far produrre all'AI la
 * valutazione P×D per ogni pericolo selezionato. I risultati sono
 * poi modificabili card-by-card (PericoloCard).
 */
export default function StepPericoli({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [catalog, setCatalog] = useState({ gruppi: [], totale: 0 });
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState([]);
  const [ctx, setCtx] = useState("");
  const [filter, setFilter] = useState("");
  const s1 = dvr?.steps?.step1_dati_generali || {};
  // Elenco mansioni disponibili per il multi-select "mansioni esposte" nei singoli pericoli
  const mansioniOptions = useMemo(() => {
    const arr = dvr?.steps?.step4_mansioni || [];
    return Array.from(new Set(arr.map((m) => (m?.mansione || "").trim()).filter(Boolean)));
  }, [dvr]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/dvr/hazards-catalog");
        if (alive) setCatalog(r.data || { gruppi: [], totale: 0 });
      } catch (err) {
        if (alive) toast.error("Impossibile caricare il catalogo pericoli");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const selectedIds = useMemo(() => new Set(sel.map((h) => h.id)), [sel]);
  const alreadyGeneratedIds = useMemo(
    () => new Set((list || []).map((h) => h._catalog_id).filter(Boolean)),
    [list]
  );

  const toggle = (item) => {
    setSel((s) =>
      s.some((x) => x.id === item.id) ? s.filter((x) => x.id !== item.id) : [...s, item]
    );
  };

  const filteredGroups = useMemo(() => {
    if (!filter.trim()) return catalog.gruppi;
    const q = filter.toLowerCase();
    return catalog.gruppi
      .map((g) => ({
        ...g,
        items: g.items.filter((it) => it.nome.toLowerCase().includes(q) || (it.normativa || "").toLowerCase().includes(q)),
      }))
      .filter((g) => g.items.length > 0);
  }, [catalog, filter]);

  const generate = async () => {
    if (sel.length === 0) return toast.error("Seleziona almeno un pericolo");
    setAiLoading(true);
    try {
      const contesto = [ctx.trim(), s1.ateco && `ATECO: ${s1.ateco}`, s1.tipo_attivita && `Attività: ${s1.tipo_attivita}`]
        .filter(Boolean)
        .join(" · ");
      const r = await api.post("/dvr/ai/hazards", {
        pericoli: sel.map((s) => ({ id: s.id, nome: s.nome, normativa: s.normativa, categoria: s.categoria, gruppo: s.gruppo })),
        contesto: contesto || "n.d.",
      });
      const results = Array.isArray(r.data) ? r.data : (r.data?.pericoli || []);
      if (!results.length) {
        toast.error("L'AI non ha restituito risultati");
      } else {
        // Enrich each with _uid + carry over id/gruppo/categoria per riferimento
        const enriched = results.map((res, i) => ({
          ...res,
          _uid: uid(),
          _catalog_id: sel[i]?.id,
          _categoria: sel[i]?.categoria,
          _gruppo: sel[i]?.gruppo,
        }));
        onChange([...list, ...enriched]);
        toast.success(`${enriched.length} pericoli valutati`);
        setSel([]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    }
    setAiLoading(false);
  };

  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const updateItem = (i, next) => onChange(list.map((it, idx) => (idx === i ? next : it)));

  return (
    <div className="space-y-4" data-testid="step-pericoli">
      <p className="text-sm text-muted-foreground">
        Seleziona i pericoli applicabili all'azienda dal <b>catalogo standardizzato</b>{" "}
        (Procedura Standardizzata D.I. 30/11/2012 — {catalog.totale} pericoli). Clicca <b>"Genera valutazioni con AI"</b>{" "}
        per far produrre P·D·R, misure di prevenzione e norme di riferimento. Ogni risultato è modificabile manualmente.
      </p>

      {/* Ricerca + contesto */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Cerca nel catalogo (nome o normativa)…"
            className="pl-9"
            data-testid="s5-filter"
          />
        </div>
        <Button
          onClick={generate}
          disabled={aiLoading || sel.length === 0}
          className="rounded-full"
          data-testid="s5-gen"
        >
          {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Genera valutazioni ({sel.length})
        </Button>
      </div>

      <Textarea
        rows={2}
        placeholder="Contesto azienda (facoltativo — verrà passato all'AI insieme ai pericoli selezionati)"
        value={ctx}
        onChange={(e) => setCtx(e.target.value)}
        data-testid="s5-context"
      />

      {/* Catalogo raggruppato */}
      {loading ? (
        <div className="text-sm text-muted-foreground italic">Caricamento catalogo pericoli…</div>
      ) : (
        <div className="border border-border rounded-md divide-y divide-border" data-testid="s5-catalog">
          {filteredGroups.map((g) => (
            <details key={g.nome} open className="group">
              <summary className="cursor-pointer px-3 py-2 bg-muted/30 hover:bg-muted/50 text-sm font-semibold flex items-center justify-between">
                <span>{g.nome}</span>
                <span className="text-[11px] text-muted-foreground">
                  {g.items.filter((it) => selectedIds.has(it.id)).length}/{g.items.length}
                </span>
              </summary>
              <div className="p-2 space-y-1">
                {g.items.map((it) => {
                  const isSel = selectedIds.has(it.id);
                  const already = alreadyGeneratedIds.has(it.id);
                  return (
                    <button
                      type="button"
                      key={it.id}
                      onClick={() => toggle(it)}
                      className={`w-full text-left px-3 py-1.5 rounded flex items-start gap-2 border transition ${
                        isSel
                          ? "bg-primary/10 border-primary/40"
                          : "border-transparent hover:bg-muted/40"
                      }`}
                      data-testid={`s5-hazard-${it.id}`}
                    >
                      <span
                        className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          isSel ? "bg-primary border-primary" : "border-input"
                        }`}
                      >
                        {isSel && <Check className="w-3 h-3 text-primary-foreground" />}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="text-sm font-medium block">
                          {it.nome}
                          {already && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
                              già valutato
                            </span>
                          )}
                        </span>
                        <span className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                          <span className={`px-1.5 py-0.5 rounded border ${CAT_BADGE[it.categoria] || ""}`}>
                            {it.categoria}
                          </span>
                          <span className="truncate">{it.normativa}</span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </details>
          ))}
        </div>
      )}

      {/* Risultati generati (editabili) */}
      <div className="space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
          Valutazioni generate ({list.length})
        </div>
        {list.map((h, i) => (
          <PericoloCard
            key={h._uid || `haz-${h.pericolo}-${i}`}
            item={h}
            index={i}
            onSave={(next) => updateItem(i, next)}
            onRemove={() => remove(i)}
            mansioniOptions={mansioniOptions}
          />
        ))}
        {list.length === 0 && (
          <div className="text-sm text-muted-foreground italic border border-dashed border-border rounded p-6 text-center">
            Seleziona i pericoli dalla checklist e clicca "Genera valutazioni".
          </div>
        )}
      </div>
    </div>
  );
}
